import {
  Component,
  computed,
  effect,
  ElementRef,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { TranscriptionSegment } from '../../models/translated-segment.model';
import { TranscriptionSegmentElementComponent } from '../transcription-segment-element/transcription-segment-element.component';
import { Job } from '../../class/job.class';

@Component({
  selector: 'app-transcription-segments-line',
  imports: [TranscriptionSegmentElementComponent],
  template: `
    <header>
      Vocal
      @if (index()) {
        <span>{{ index() }}</span>
      }
    </header>
    <div class="vocal" #vocal>
      @for (transcriptionSegment of segments(); track $index) {
        <app-transcription-segment-element
          [class.active]="transcriptionSegment === activeSegement()"
          (onClick)="onSegmentClick($event)"
          [color]="color()"
          [style.left.px]="getStartPosition(transcriptionSegment.start)"
          [style.width.px]="getWidth(transcriptionSegment)"
          [segment]="transcriptionSegment"
        />
      }
    </div>
  `,
  styleUrl: './transcription-segment-line.component.scss',
})
export class TranscriptionSegmentsLineComponent {
  color: InputSignal<string> = input('#93579F');
  segments: InputSignal<TranscriptionSegment[]> = input.required();
  job: InputSignal<Job> = input.required();
  duration: Signal<number | null | undefined> = computed(
    () => this.job()?.duration,
  );
  index: InputSignal<number | undefined> = input();

  segmentClick: OutputEmitterRef<TranscriptionSegment | undefined> = output();

  vocalContainerWidth: WritableSignal<number> = signal(0);

  vocalRef: Signal<ElementRef<HTMLDivElement>> = viewChild.required('vocal', {
    read: ElementRef<HTMLDivElement>,
  });

  activeSegement: WritableSignal<TranscriptionSegment | undefined> =
    signal(undefined);

  constructor() {
    effect((onCleanup) => {
      const el: HTMLDivElement = this.vocalRef().nativeElement;
      const observer: ResizeObserver = new ResizeObserver(() => {
        this.vocalContainerWidth.set(el.clientWidth);
      });
      observer.observe(el);
      this.vocalContainerWidth.set(el.clientWidth);
      onCleanup(() => observer.disconnect());
    });
  }

  onSegmentClick(segment: TranscriptionSegment): void {
    const isAlreadyActive: boolean = segment === this.activeSegement();
    this.activeSegement.set(isAlreadyActive ? undefined : segment);
    this.segmentClick.emit(isAlreadyActive ? undefined : segment);
  }

  getStartPosition(start: number): number | null {
    if (!this.duration()) return null;
    const duration: number = this.duration()!;
    const contentSize: number = this.vocalContainerWidth();
    return (contentSize * start) / duration;
  }

  getWidth(segment: TranscriptionSegment): number | null {
    if (!this.duration()) return null;
    const segmentDuration: number = segment.end - segment.start;
    const duration: number = this.duration()!;
    const contentSize: number = this.vocalContainerWidth();
    return (contentSize * segmentDuration) / duration;
  }
}
